import { useMemo } from 'react';

import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

type AssistanceServiceParams = AssistanceServiceInputParam['assistanceServiceParams'];
type AssistanceServiceParamsKey = keyof AssistanceServiceParams;
type AssistanceServiceParamsDependencyKey =
	| 'touchpointSource'
	| 'product'
	| 'experienceId'
	| 'siteId'
	| 'headers'
	| 'baseUrl'
	| 'minionAlias'
	| 'fileValidatePath'
	| 'fileUploadPath'
	| 'channelId';
type EnsureNever<T extends never> = unknown;
type AssistanceServiceParamsDependencyCoverage = EnsureNever<
	Exclude<AssistanceServiceParamsKey, AssistanceServiceParamsDependencyKey>
> &
	EnsureNever<Exclude<AssistanceServiceParamsDependencyKey, AssistanceServiceParamsKey>>;

const getHeadersSignature = (headers: AssistanceServiceParams['headers']): string | undefined => {
	if (!headers) {
		return undefined;
	}

	// Assistance service headers are a flat string map; sorted entries make
	// equivalent objects stable even when insertion order changes.
	return JSON.stringify(
		Object.entries(headers).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
	);
};

export const useBoundaryAssistanceServiceParams = (
	assistanceServiceParams: AssistanceServiceParams,
	isRenderStabilizationEnabled: boolean,
): AssistanceServiceParams & AssistanceServiceParamsDependencyCoverage => {
	const headersSignature = useMemo(
		() =>
			isRenderStabilizationEnabled
				? getHeadersSignature(assistanceServiceParams.headers)
				: undefined,
		[assistanceServiceParams.headers, isRenderStabilizationEnabled],
	);
	const {
		touchpointSource,
		product,
		experienceId,
		siteId,
		baseUrl,
		minionAlias,
		fileValidatePath,
		fileUploadPath,
		channelId,
	} = assistanceServiceParams;
	// Use the header signature, not the raw header object, so equivalent header
	// object churn does not invalidate the memo boundary.
	const headers = headersSignature;

	const stableAssistanceServiceParams = useMemo(() => {
		if (!isRenderStabilizationEnabled) {
			return assistanceServiceParams;
		}

		return {
			...assistanceServiceParams,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- scalar fields below represent the object without preserving parent identity churn.
	}, [
		isRenderStabilizationEnabled,
		touchpointSource,
		product,
		experienceId,
		siteId,
		headers,
		baseUrl,
		minionAlias,
		fileValidatePath,
		fileUploadPath,
		channelId,
	]);

	return isRenderStabilizationEnabled ? stableAssistanceServiceParams : assistanceServiceParams;
};
